import { cn } from '@heidi/utils';
import { m } from 'motion/react';
import { useId, useRef, useState } from 'react';

import { useWorkHomeMarkGlint } from './work-home-mark-glint';
import {
  getPrefersReducedMotion,
  useWorkHomeMarkEntrance,
  type WorkHomeMarkEntranceMode,
} from './work-home-mark-motion';
import {
  halfCropDefs,
  TRACE_GLASS_EDGE,
  traceStrokePaths,
} from './work-home-mark-trace-render';

// The pointer-follow constants live with the light loop; re-exported so
// existing importers (and the co-located test) resolve unchanged.
export { FOLLOW_TIME_MS, MAX_HIGHLIGHT_OPACITY } from './work-home-mark-glint';

// The Heidi mark above the Work-home composer, drawn as a GLASS-EDGED OUTLINE:
// three concurrent pens trace the logo's contours as the composer wave rises,
// the outline rises 12px while it draws, and what stays on screen is the
// stroked outline alone — no fill, no body, a faint refracted edge (see
// `TRACE_GLASS_EDGE`) in the top three quarters of the logo, melting into the
// composer through a bottom fade. On a composer submit the outline pinches
// into nothing (see `useWorkHomeMarkEntrance`). The pointer-follow glint
// (`work-home-mark-glint.ts`) lights the same outline with a light-direction
// gradient once the draw has settled.

// The Heidi logo mark as one compound path in a 48-unit box, inline because
// the trace needs the raw geometry `HeidiLogoMark` doesn't expose. This full compound path is what the pointer-follow ring
// strokes; the DRAWN outline is `TRACE_PEN_PIECES` (work-home-mark-trace-path.ts),
// generated from this same path and cut to the crop below.
const MARK_PATH =
  'M24.9502 2.99512C27.544 0.984356 30.8536 -0.158054 34.4336 0.0175781C41.7466 0.373357 47.6634 6.29128 47.9854 13.5732V13.5752C48.1452 17.1599 46.9697 20.4677 44.9141 23.0459C44.4707 23.6019 44.4709 24.3967 44.9141 24.9551C46.9696 27.5354 48.1451 30.8412 47.9854 34.4258C47.6611 41.7076 41.7445 47.6275 34.4316 47.9834C30.8518 48.1568 27.542 47.0175 24.9482 45.0068C24.3921 44.5768 23.6056 44.5768 23.0518 45.0068C20.458 47.0175 17.1482 48.159 13.5684 47.9834C6.25319 47.6253 0.338868 41.7077 0.0146484 34.4258C-0.145114 30.8412 1.02938 27.5377 3.08496 24.9551C3.52844 24.3989 3.52849 23.6043 3.08496 23.0459C1.02946 20.4655 -0.145204 17.1597 0.0146484 13.5752C0.341271 6.29351 6.25762 0.373559 13.5703 0.0175781C17.1503 -0.158054 20.4609 0.982104 23.0547 2.99512C23.6108 3.42484 24.3965 3.42502 24.9502 2.99512ZM36.4756 3.25195C31.8374 2.07657 27.4853 3.61424 24.6709 6.60449C24.3085 6.98934 23.6984 6.98925 23.3359 6.60449C20.5216 3.61204 16.1693 2.07668 11.5312 3.25195C7.43356 4.29221 4.20233 7.50701 3.15527 11.5869C1.97547 16.2006 3.51728 20.5358 6.51855 23.3369C6.90582 23.6972 6.90582 24.3048 6.51855 24.665C3.51745 27.4683 1.97338 31.8006 3.15527 36.4141C4.19999 40.4941 7.4312 43.712 11.5312 44.75C16.1693 45.9252 20.5216 44.3876 23.3359 41.3975C23.6984 41.0125 24.3084 41.0125 24.6709 41.3975C27.4853 44.3899 31.8375 45.9254 36.4756 44.75C40.5734 43.7097 43.8046 40.4941 44.8516 36.4141C46.0312 31.8005 44.4885 27.4661 41.4873 24.665C41.1003 24.3049 41.1004 23.6972 41.4873 23.3369C44.4886 20.5358 46.0336 16.2006 44.8516 11.5869C43.8068 7.50698 40.5755 4.28996 36.4756 3.25195ZM23.999 25.542C31.2174 25.542 34.1446 26.4269 36.5312 27.8545C39.035 29.3519 40.4717 31.8582 40.4717 34.7246C40.4716 38.1942 37.252 40.7878 34.377 40.7881C31.9228 40.7881 29.7652 39.552 28.457 37.3994C27.4282 35.704 26.7503 33.8216 26.5342 29.9873C26.4892 29.1543 25.8029 28.4988 24.9678 28.4873C24.6548 28.4805 24.3342 28.4805 24.001 28.4805C23.6679 28.4805 23.348 28.4828 23.0352 28.4873C22.2 28.4987 21.516 29.1543 21.4688 29.9873C21.2526 33.8193 20.577 35.7017 19.5459 37.3994C18.24 39.552 16.0802 40.7881 13.626 40.7881H13.6221C10.7446 40.7881 7.52743 38.1944 7.52734 34.7246C7.52734 31.856 8.9632 29.3519 11.4668 27.8545C13.8534 26.4269 16.7807 25.542 23.999 25.542ZM17.6143 28.8477C15.1713 29.2057 13.9726 29.7844 12.9932 30.3721V30.376H12.9912C10.8096 31.6819 10.4834 33.6615 10.4834 34.7266C10.4834 36.4084 12.1873 37.8523 13.626 37.8525C15.0647 37.8525 16.2382 37.1678 17.0195 35.8799C17.8278 34.5491 18.3589 32.9993 18.5322 29.6758C18.5567 29.174 18.116 28.7759 17.6143 28.8477ZM30.3955 28.8457C29.8935 28.7716 29.4528 29.1696 29.4775 29.6738C29.6509 32.9971 30.1843 34.5463 30.9902 35.877C31.7714 37.167 32.9668 37.8475 34.3828 37.8477C35.7989 37.8477 37.5261 36.4067 37.5264 34.7227C37.5264 33.6599 37.2023 31.6781 35.0186 30.3721V30.3701H35.0156C34.0362 29.7847 32.8406 29.206 30.3955 28.8457ZM34.3809 7.21289C37.2582 7.21308 40.4756 9.80761 40.4756 13.2773C40.4755 16.1458 39.0387 18.6492 36.5352 20.1465C34.1485 21.5741 31.2214 22.459 24.0029 22.459C16.7847 22.459 13.8573 21.574 11.4707 20.1465C8.9674 18.6491 7.53136 16.1434 7.53125 13.2773C7.53125 9.80749 10.7508 7.21289 13.626 7.21289C16.0799 7.21293 18.2368 8.44925 19.5449 10.6016C20.5739 12.2971 21.2516 14.18 21.4678 18.0146C21.5128 18.8477 22.1999 19.5034 23.0352 19.5146C23.3458 19.5214 23.6678 19.5205 24.001 19.5205C24.3342 19.5205 24.6539 19.5192 24.9668 19.5146C25.8021 19.5034 26.4869 18.8477 26.5342 18.0146C26.7503 14.1823 27.4258 12.2993 28.457 10.6016C29.7629 8.44925 31.922 7.21291 34.376 7.21289H34.3809ZM13.6191 10.1543C12.2029 10.1543 10.4756 11.595 10.4756 13.2793C10.4756 14.3421 10.8004 16.3239 12.9844 17.6299V17.6318H12.9863C13.9657 18.2173 15.1614 18.796 17.6064 19.1562C18.1085 19.2306 18.5502 18.8315 18.5254 18.3271C18.352 15.0017 17.8177 13.4547 17.0117 12.124C16.2304 10.834 15.0352 10.1543 13.6191 10.1543ZM34.376 10.1514C32.9373 10.1514 31.7647 10.8342 30.9834 12.1221C30.1751 13.4528 29.6431 15.0018 29.4697 18.3252C29.445 18.8273 29.8866 19.2264 30.3887 19.1543C32.8315 18.7963 34.0294 18.2175 35.0088 17.6299L35.0107 17.627C37.1923 16.3211 37.5195 14.3425 37.5195 13.2773C37.5195 11.5953 35.8147 10.1514 34.376 10.1514Z';

// Rendered width of the mark (px). The svg's width/height attributes read it,
// so no width class is involved; the stroke widths are viewBox user units and
// scale with it.
export const MARK_WIDTH = 150;

// The crop: the top three quarters of the 48-unit logo (viewBox y -2..36) —
// enough to show the bottom lobes' upper halves above the fade — with a
// white-to-transparent mask so the bottom melts into the composer.
const CROP_HEIGHT_UNITS = 38;
const CROP_VIEWBOX = `-2 -2 52 ${CROP_HEIGHT_UNITS}`;
const cropHeight = (width: number): number =>
  Math.round((width * CROP_HEIGHT_UNITS) / 52);
// The layout box the composer position was tuned around: a 100px-wide mark in
// a 52 x 32 crop (62px tall). The mark's grid row in WorkHomeScene is an
// `auto` track bottom-aligned onto the composer's gap, so a taller svg would
// push the composer down by the difference; this negative top margin keeps
// the mark's layout box at that height whatever its rendered size, and the
// extra height grows upward into the empty space above.
const SLOT_HEIGHT_PX = Math.round((100 * 32) / 52);
export const MARK_SLOT_TOP_OFFSET_PX = SLOT_HEIGHT_PX - cropHeight(MARK_WIDTH);

export interface WorkHomeMarkProps {
  className?: string;
  /** Rendered width in pixels. The Work-home hero keeps the 150px default. */
  width?: number;
  /**
   * `'wave'` ONLY from a surface that renders `WorkHomeComposerWave` — the
   * draw is timed off that wave's rise. Everywhere else the default
   * `'immediate'` shows the resting outline on the first paint; see
   * `useWorkHomeMarkEntrance`.
   */
  entrance?: WorkHomeMarkEntranceMode;
  /**
   * The composer's submit signal: true for the duration of a submit. The
   * outline pinches into nothing the instant it turns on and hides; if it
   * turns off while the mark is still mounted (the submit failed and the
   * composer stayed), the outline re-draws. Under reduced motion it hides
   * and restores instantly.
   */
  submitting?: boolean;
}

export const WorkHomeMark = ({
  className,
  width = MARK_WIDTH,
  entrance = 'immediate',
  submitting = false,
}: WorkHomeMarkProps): JSX.Element => {
  const id = useId();
  const markRef = useRef<SVGSVGElement>(null);
  const highlightRef = useRef<SVGGElement>(null);
  const directionRef = useRef<SVGLinearGradientElement>(null);
  // The glint loop can also drive a sheen and two edge-glint gradients; the
  // stroked mark renders none of them, so these refs never attach and the
  // loop's own null-guards skip them.
  const sheenRef = useRef<SVGRadialGradientElement>(null);
  const edgeLightRef = useRef<SVGLinearGradientElement>(null);
  const edgeShadowRef = useRef<SVGLinearGradientElement>(null);

  // Read ONCE at mount (a single `matchMedia` read) and shared by the
  // entrance and the glint.
  const [prefersReducedMotion] = useState(getPrefersReducedMotion);
  const { hasSettledRef, collapsed, entranceMotionProps, traceStrokes } =
    useWorkHomeMarkEntrance(entrance, prefersReducedMotion, submitting);

  useWorkHomeMarkGlint({
    markRef,
    highlightRef,
    directionRef,
    sheenRef,
    edgeLightRef,
    edgeShadowRef,
    hasSettledRef,
    ambientEnabled: !prefersReducedMotion,
    // Peak alphas the loop would write to the sheen / edge-light gradients if
    // they were rendered; unused by the stroked mark (see the refs above).
    baseOpacity: { sheenNear: 0.85, sheenMid: 0.22, edgeLightPeak: 0.45 },
  });

  return (
    <m.svg
      ref={markRef}
      data-testid="work-home-mark"
      aria-hidden="true"
      focusable="false"
      width={width}
      height={cropHeight(width)}
      viewBox={CROP_VIEWBOX}
      className={cn(
        'mx-auto block max-w-full shrink-0 select-none text-accent-yellow',
        // Submit-collapse end state: hidden, not unmounted. A class rather
        // than a `style.visibility` entry because Motion routes non-motion
        // style keys on an SVG root as attributes.
        collapsed && 'invisible',
        className
      )}
      style={{
        ...entranceMotionProps.style,
        marginTop: SLOT_HEIGHT_PX - cropHeight(width),
      }}
    >
      <defs>
        <path id={`${id}-shape`} d={MARK_PATH} />
        {halfCropDefs(id, CROP_HEIGHT_UNITS)}
        {/*
          The pointer-follow ring's light-direction gradient. The glint loop
          rewrites its x1/y1/x2/y2 every frame through `directionRef`. Scoped
          to `text-text-primary-on-dark` (always white) rather than the yellow
          currentColor, so the glint reads as light on the edge rather than a
          second tint of it.
        */}
        <g className="text-text-primary-on-dark">
          <linearGradient
            ref={directionRef}
            id={`${id}-direction`}
            gradientUnits="userSpaceOnUse"
            x1="-10"
            y1="24"
            x2="58"
            y2="24"
          >
            <stop offset="0" stopColor="currentColor" stopOpacity="0" />
            <stop offset="0.5" stopColor="currentColor" stopOpacity="0.5" />
            <stop offset="1" stopColor="currentColor" stopOpacity="1" />
          </linearGradient>
        </g>
      </defs>
      <g strokeWidth="0.12" mask={`url(#${id}-mask)`}>
        {/* The drawn outline IS the mark: one glass-edged stroke group per pen piece. */}
        {traceStrokePaths(traceStrokes, TRACE_GLASS_EDGE)}
        <g ref={highlightRef} opacity="0">
          <use href={`#${id}-shape`} fill="none" stroke={`url(#${id}-direction)`} />
        </g>
      </g>
    </m.svg>
  );
};
